import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Play, Pause, Lock, ShoppingCart, ExternalLink, Crown, Filter, Search, Disc, Package } from "lucide-react";
import { track as trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AnimatedSection from "@/components/AnimatedSection";
import { getFileUrl } from "@/components/FileUpload";
import { useCheckout } from "@/components/CheckoutModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREVIEW_LIMIT = 30;

function TrackCard({ track, onBuy }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [locked, setLocked] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [buying, setBuying] = useState(false);
  const effectivePrice = (track.sale_price > 0 && track.sale_price < track.price) ? track.sale_price : track.price;
  const isPaid = !track.is_free && effectivePrice > 0;
  const hasPreview = !!track.audio_preview;

  const stop = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setPlaying(false); setProgress(0);
  };

  const togglePlay = () => {
    if (locked || !audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else {
      audioRef.current.play(); setPlaying(true);
      trackEvent("music_play", { target_id: track?.id, target_type: "music", target_label: track?.title });
    }
  };

  const onTimeUpdate = () => {
    if (!audioRef.current) return;
    const t = audioRef.current.currentTime;
    const d = audioRef.current.duration;
    if (isPaid && t >= PREVIEW_LIMIT) {
      audioRef.current.pause(); audioRef.current.currentTime = PREVIEW_LIMIT;
      setPlaying(false); setLocked(true); setProgress(100); return;
    }
    const max = isPaid ? Math.min(d, PREVIEW_LIMIT) : d;
    setProgress(max > 0 ? (t / max) * 100 : 0);
  };

  const seek = (e) => {
    if (!audioRef.current?.duration || locked) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const max = isPaid ? Math.min(audioRef.current.duration, PREVIEW_LIMIT) : audioRef.current.duration;
    audioRef.current.currentTime = pct * max;
  };

  const fmt = (s) => { const m = Math.floor(s / 60); const sec = Math.floor(s % 60); return `${m}:${sec.toString().padStart(2, "0")}`; };
  const dispDur = isPaid ? Math.min(duration, PREVIEW_LIMIT) : duration;
  const dispTime = isPaid ? Math.min(audioRef.current?.currentTime || 0, PREVIEW_LIMIT) : (audioRef.current?.currentTime || 0);

  return (
    <div className={`bg-[#0A0A0A] border overflow-hidden group transition-all duration-500 ${
      track.exclusive
        ? "border-[#8B0000]/30 hover:border-[#8B0000]/60 shadow-[0_0_20px_rgba(139,0,0,0.1)] hover:shadow-[0_0_30px_rgba(139,0,0,0.2)]"
        : "border-white/5 hover:border-[#D4AF37]/20"
    }`}>
      {/* Hidden audio element */}
      {hasPreview && (
        <audio
          ref={audioRef}
          src={getFileUrl(track.audio_preview)}
          onTimeUpdate={onTimeUpdate}
          onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
          onEnded={() => { if (isPaid) setLocked(true); setPlaying(false); setProgress(isPaid ? 100 : 0); }}
        />
      )}

      {/* Cover Art with Play overlay */}
      <div className="relative aspect-square overflow-hidden">
        {track.cover_art ? (
          <img src={getFileUrl(track.cover_art)} alt={track.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full bg-[#111111]" />
        )}

        {/* Dark overlay on hover for play button */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-500" />

        {/* Central Play button */}
        {hasPreview && !locked && (
          <button
            data-testid={`play-btn-${track.id}`}
            onClick={togglePlay}
            className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
              playing
                ? "bg-[#D4AF37] scale-100 opacity-100"
                : "bg-[#D4AF37]/90 scale-90 opacity-0 group-hover:scale-100 group-hover:opacity-100"
            }`}
          >
            {playing ? <Pause className="w-7 h-7 text-black" /> : <Play className="w-7 h-7 text-black ml-1" />}
          </button>
        )}

        {/* Locked state overlay */}
        {locked && (
          <div data-testid={`lock-overlay-${track.id}`} className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center z-20 backdrop-blur-sm">
            <div className="w-14 h-14 rounded-full bg-[#8B0000]/30 border border-[#8B0000]/50 flex items-center justify-center mb-3">
              <Lock className="w-6 h-6 text-[#8B0000]" />
            </div>
            <p data-testid={`unlock-message-${track.id}`} className="text-sm font-semibold text-white/80 mb-1">Unlock Full Track</p>
            <p className="text-[10px] text-white/30 mb-3">Preview ended</p>
            <button
              data-testid={`replay-btn-${track.id}`}
              onClick={() => { setLocked(false); stop(); }}
              className="text-[10px] text-[#D4AF37] hover:text-white transition-colors uppercase tracking-wider font-semibold border border-[#D4AF37]/30 px-4 py-1.5 hover:border-[#D4AF37]"
            >
              Replay Preview
            </button>
          </div>
        )}

        {/* Exclusive banner */}
        {track.exclusive && (
          <div data-testid={`exclusive-badge-${track.id}`} className="absolute top-0 left-0 right-0 z-30">
            <div className="bg-gradient-to-r from-[#8B0000] via-[#B22222] to-[#8B0000] px-4 py-2 flex items-center justify-center gap-2">
              <Crown className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-white">Website Exclusive</span>
              <Crown className="w-3.5 h-3.5 text-[#D4AF37]" />
            </div>
          </div>
        )}

        {/* Featured badge */}
        {track.featured && (
          <div className="absolute left-3 z-20" style={{ top: track.exclusive ? "44px" : "12px" }}>
            <span className="text-[10px] uppercase bg-[#D4AF37] text-black px-2 py-1 rounded font-bold">Latest</span>
          </div>
        )}

        {/* Preview progress bar — bottom of artwork */}
        {hasPreview && playing && !locked && (
          <div className="absolute bottom-0 left-0 right-0 z-20 px-4 pb-3 pt-8 bg-gradient-to-t from-black/80 to-transparent">
            <div className="relative h-1 bg-white/15 rounded-full cursor-pointer" onClick={seek}>
              <div className="absolute inset-y-0 left-0 bg-[#D4AF37] rounded-full transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[9px] text-white/40 tabular-nums">{fmt(dispTime)}</span>
              <span className="text-[9px] text-white/40 tabular-nums">{dispDur > 0 ? fmt(dispDur) : "--:--"}</span>
            </div>
          </div>
        )}
      </div>

      {/* Info + Actions bar */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0 flex-1">
            <p data-testid={`artist-name-${track.id}`} className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] mb-1">{track.artist_name || "Luxifer 6"}</p>
            <h3 data-testid={`track-title-${track.id}`} className="font-heading text-xl leading-tight truncate">{track.title}</h3>
          </div>
          {(() => {
            const effectivePrice = (track.sale_price > 0 && track.sale_price < track.price) ? track.sale_price : track.price;
            const isPaidTrack = !track.is_free && effectivePrice > 0;
            if (!isPaidTrack) {
              return (
                <span data-testid={`track-price-${track.id}`} className="text-xs uppercase tracking-wider text-white/30 font-semibold flex-shrink-0 mt-1">Free</span>
              );
            }
            const onSale = track.sale_price > 0 && track.sale_price < track.price;
            return (
              <div className="flex-shrink-0 text-right">
                {onSale && <span className="text-[11px] text-white/30 line-through mr-1.5">${track.price.toFixed(2)}</span>}
                <span data-testid={`track-price-${track.id}`} className="text-lg font-bold text-[#D4AF37]">${effectivePrice.toFixed(2)}</span>
                {onSale && <p className="text-[9px] uppercase tracking-wider text-[#8B0000] font-bold">On Sale</p>}
              </div>
            );
          })()}
        </div>

        {track.explicit && (
          <span className="inline-block text-[9px] uppercase text-white/70 border border-white/20 px-1.5 py-0.5 rounded font-bold mb-2 mr-1.5">Explicit</span>
        )}
        {track.genre && (
          <span className="inline-block text-[9px] uppercase text-white/40 border border-white/10 px-1.5 py-0.5 rounded mb-2">{track.genre}</span>
        )}
        {track.description && <p className="text-white/35 text-xs mb-4 line-clamp-2">{track.description}</p>}

        {/* Action buttons */}
        <div className="flex gap-2">
          {hasPreview && !locked && (
            <Button
              data-testid={`preview-btn-${track.id}`}
              onClick={togglePlay}
              variant="outline"
              className={`text-xs h-10 uppercase tracking-wider font-semibold transition-all duration-300 ${
                playing
                  ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                  : "border-white/10 text-white/50 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"
              } ${track.price > 0 ? "" : "flex-1"}`}
            >
              {playing ? <><Pause className="w-3.5 h-3.5 mr-1.5" /> Playing</> : <><Play className="w-3.5 h-3.5 mr-1.5" /> Preview</>}
              {isPaid && <span className="ml-1.5 text-[9px] opacity-50">0:{PREVIEW_LIMIT}</span>}
            </Button>
          )}
          {isPaid ? (
            <Button
              data-testid={`buy-btn-${track.id}`}
              onClick={() => onBuy(track.id)}
              disabled={buying}
              className="flex-1 bg-[#D4AF37] text-black hover:bg-white text-xs h-10 uppercase tracking-wider font-semibold"
            >
              <ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> Buy Now
            </Button>
          ) : track.download_file ? (
            <Button data-testid={`download-btn-${track.id}`} asChild className="flex-1 bg-[#D4AF37] text-black hover:bg-white text-xs h-10 uppercase tracking-wider font-semibold">
              <a href={getFileUrl(track.download_file)} download>Download Free</a>
            </Button>
          ) : null}
        </div>

      </div>
    </div>
  );
}

function AlbumCard({ album, onBuy }) {
  const [buying, setBuying] = useState(false);
  const cover = album.cover_art ? getFileUrl(album.cover_art) : "";
  const effective = (album.sale_price > 0 && album.sale_price < album.price) ? album.sale_price : album.price;
  const isPaid = !album.is_free && effective > 0;
  const onSale = album.sale_price > 0 && album.sale_price < album.price;
  const trackCount = (album.tracks || album.track_ids || []).length;

  const handleBuy = async () => {
    setBuying(true);
    try { await onBuy(album.id); } catch (e) { toast.error(e?.message || "Checkout failed"); }
    finally { setBuying(false); }
  };

  return (
    <div className="bg-[#111111] border border-white/5 rounded-lg overflow-hidden hover:border-[#D4AF37]/30 transition-all duration-500 hover:shadow-[0_20px_60px_-20px_rgba(212,175,55,0.4)] group">
      <div className="aspect-square bg-[#0A0A0A] relative overflow-hidden">
        {cover ? (
          <img src={cover} alt={album.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Disc className="w-16 h-16 text-white/15" /></div>
        )}
        <span className="absolute top-3 left-3 text-[9px] uppercase tracking-wider font-bold bg-[#D4AF37] text-black px-2 py-1 rounded">
          {(album.album_type || "ep").toUpperCase()}
        </span>
        {album.edition_type && album.edition_type !== "standard" && (
          <span className="absolute top-3 right-3 text-[9px] uppercase tracking-wider font-bold bg-[#8B0000] text-white px-2 py-1 rounded">
            {album.edition_type}
          </span>
        )}
        {album.exclusive && (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3">
            <span className="text-[10px] uppercase tracking-widest text-[#ff4444] font-bold flex items-center gap-1">
              <Crown className="w-3 h-3" /> Exclusive
            </span>
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <h3 className="font-heading text-xl tracking-wide truncate">{album.title}</h3>
            <p className="text-white/40 text-xs mt-0.5 flex items-center gap-1.5">
              <Package className="w-3 h-3" /> {trackCount} tracks
              {album.release_year && ` · ${album.release_year}`}
            </p>
          </div>
          {isPaid ? (
            <div className="text-right flex-shrink-0">
              {onSale && <p className="text-[11px] text-white/30 line-through">${album.price.toFixed(2)}</p>}
              <p className="text-lg font-bold text-[#D4AF37]">${effective.toFixed(2)}</p>
              {onSale && <p className="text-[9px] uppercase tracking-wider text-[#8B0000] font-bold">On Sale</p>}
            </div>
          ) : (
            <p className="text-xs uppercase tracking-wider text-white/30 font-semibold flex-shrink-0 mt-1">Free</p>
          )}
        </div>
        {album.genre && <span className="inline-block text-[9px] uppercase text-white/40 border border-white/10 px-1.5 py-0.5 rounded mb-3">{album.genre}</span>}
        {album.description && <p className="text-white/35 text-xs mb-4 line-clamp-2">{album.description}</p>}

        <Button
          data-testid={`album-buy-btn-${album.id}`}
          onClick={handleBuy}
          disabled={buying || !isPaid}
          className="w-full bg-[#D4AF37] text-black hover:bg-white text-xs h-11 uppercase tracking-wider font-semibold"
        >
          <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />
          {isPaid ? `Buy Full ${(album.album_type || "EP").toUpperCase()}` : "Free Download"}
        </Button>
      </div>
    </div>
  );
}

export default function Music() {
  const [tracks, setTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exclusiveOnly, setExclusiveOnly] = useState(false);
  const [releaseType, setReleaseType] = useState("all"); // all | single | ep | album
  const [search, setSearch] = useState("");
  const { startCheckout, modal } = useCheckout();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/music?_t=${Date.now()}`),
      axios.get(`${API}/albums?_t=${Date.now()}`),
    ])
      .then(([m, a]) => { setTracks(m.data); setAlbums(a.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Sort: exclusive first, then featured, then rest
  const sorted = [...tracks].sort((a, b) => {
    if (a.exclusive !== b.exclusive) return b.exclusive ? 1 : -1;
    if (a.featured !== b.featured) return b.featured ? 1 : -1;
    return 0;
  });

  const q = search.trim().toLowerCase();
  const filtered = sorted.filter((t) => {
    if (exclusiveOnly && !t.exclusive) return false;
    if (releaseType !== "all" && (t.release_type || "single") !== releaseType) return false;
    if (q) {
      const hay = `${t.title || ""} ${t.artist_name || ""} ${t.album_name || ""} ${t.genre || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const exclusiveCount = tracks.filter(t => t.exclusive).length;
  const typeCounts = {
    all: tracks.length,
    single: tracks.filter((t) => (t.release_type || "single") === "single").length,
    ep: tracks.filter((t) => t.release_type === "ep").length,
    album: tracks.filter((t) => t.release_type === "album").length,
  };

  return (
    <div className="pt-16">
      <div className="relative py-24 sm:py-32 px-6 bg-gradient-to-b from-[#8B0000]/10 to-transparent">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">Discography</p>
          <h1 data-testid="music-page-title" className="font-heading text-5xl sm:text-6xl lg:text-7xl">MUSIC</h1>
          <p className="text-white/50 mt-4 max-w-lg">Every track is a chapter. Every beat is a heartbeat.</p>
        </div>
      </div>

      <div className="px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          {/* Search + filter bar */}
          {tracks.length > 0 && (
            <div className="mb-8 space-y-4">
              <div className="relative max-w-md">
                <Search className="w-4 h-4 text-white/25 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  data-testid="music-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search tracks, albums, genres…"
                  className="w-full bg-[#0A0A0A] border border-white/10 pl-9 pr-3 py-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/25"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {["all", "single", "ep", "album"].map((t) => (
                  <button
                    key={t}
                    data-testid={`filter-type-${t}`}
                    onClick={() => setReleaseType(t)}
                    className={`text-[11px] uppercase tracking-wider font-semibold px-3 py-1.5 border transition-all duration-300 ${
                      releaseType === t
                        ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                        : "border-white/10 text-white/40 hover:border-white/20 hover:text-white/60"
                    }`}
                  >
                    {t === "all" ? "All" : t.toUpperCase()}
                    <span className="ml-1.5 text-[9px] opacity-60">{typeCounts[t] || 0}</span>
                  </button>
                ))}
                {exclusiveCount > 0 && (
                  <button
                    data-testid="filter-exclusive-btn"
                    onClick={() => setExclusiveOnly(!exclusiveOnly)}
                    className={`flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold px-3 py-1.5 border transition-all duration-300 ${
                      exclusiveOnly
                        ? "border-[#8B0000] text-[#ff4444] bg-[#8B0000]/15"
                        : "border-white/10 text-white/40 hover:border-[#8B0000]/40 hover:text-[#ff4444]/70"
                    }`}
                  >
                    <Crown className="w-3 h-3" /> Exclusive
                    <span className={`ml-1 text-[9px] rounded-full w-4 h-4 flex items-center justify-center ${exclusiveOnly ? "bg-[#8B0000] text-white" : "bg-white/10 text-white/40"}`}>
                      {exclusiveCount}
                    </span>
                  </button>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <p className="text-white/30 text-center py-16">Loading...</p>
          ) : tracks.length === 0 && albums.length === 0 ? (
            <div className="text-center py-16 text-white/30">
              <p className="text-lg">New music coming soon.</p>
              <p className="text-sm mt-2 text-white/20">Check back for exclusive releases.</p>
            </div>
          ) : (
            <>
              {/* ALBUMS + EPs */}
              {albums.length > 0 && releaseType !== "single" && (
                <div className="mb-16">
                  <div className="flex items-end justify-between mb-6">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-wide flex items-center gap-3">
                      <Disc className="w-6 h-6 text-[#D4AF37]" /> ALBUMS & EPS
                    </h2>
                    <p className="text-white/30 text-xs uppercase tracking-wider hidden sm:block">Buy the full release · bundle savings</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {albums
                      .filter((a) => releaseType === "all" || (a.album_type || "ep") === releaseType)
                      .filter((a) => (!exclusiveOnly || a.exclusive))
                      .filter((a) => {
                        if (!q) return true;
                        const hay = `${a.title || ""} ${a.artist_name || ""} ${a.genre || ""}`.toLowerCase();
                        return hay.includes(q);
                      })
                      .map((album, index) => (
                        <AnimatedSection key={album.id} testId={`album-card-${album.id}`} delay={index * 80}>
                          <AlbumCard album={album} onBuy={startCheckout} />
                        </AnimatedSection>
                      ))}
                  </div>
                </div>
              )}

              {/* SINGLES / TRACKS */}
              {tracks.length > 0 && (
                <div>
                  {albums.length > 0 && (
                    <div className="flex items-end justify-between mb-6">
                      <h2 className="font-heading text-2xl sm:text-3xl tracking-wide">TRACKS</h2>
                    </div>
                  )}
                  {filtered.length === 0 ? (
                    <div className="text-center py-16 text-white/30">
                      <p className="text-lg">No tracks match your filters.</p>
                      <button onClick={() => { setExclusiveOnly(false); setReleaseType("all"); setSearch(""); }} className="text-sm mt-2 text-[#D4AF37] hover:text-white transition-colors">Reset filters</button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filtered.map((track, index) => (
                        <AnimatedSection key={track.id} testId={`music-track-${track.id}`} delay={index * 80}>
                          <TrackCard track={track} onBuy={startCheckout} />
                        </AnimatedSection>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {modal}
    </div>
  );
}
